import { api } from '@/lib/api'
import type { ActionResult } from '@/lib/api'

/**
 * `ExpiredDate` for the OTP request.
 *
 * Every caller built this the same way: five minutes from now, formatted
 * "YYYY-MM-DD HH:mm:ss" in LOCAL time (login.js:249-260 and the same block in
 * register.js and forgotPassword.js). Not ISO and not UTC — the upstream parses
 * it as local, so converting would move the expiry by the server's offset.
 */
export function otpExpiredDate(minutesFromNow = 5): string {
  const at = new Date()
  at.setMinutes(at.getMinutes() + minutesFromNow)

  const pad = (value: number): string => String(value).padStart(2, '0')
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`
  )
}

/**
 * Which flow the code belongs to.
 *
 * `register` activates a new account (or verifies one that never was);
 * `reset` unlocks the password-reset form. The server records this against the
 * session, because confirming the code has to land the user on the right page
 * and only the reset flow may hand out a reset ticket.
 */
export type OtpPurpose = 'register' | 'reset'

/**
 * POST account/send-otp.
 *
 * The reply carries `hash` and `redirectUrl`; the email, OTP id and expiry stay
 * server-side in the session, and the verification page reads them back from
 * GET account/otp-context. That is what the original did with TempData.
 */
export function sendOtp(email: string, purpose: OtpPurpose): Promise<ActionResult> {
  return api.post<ActionResult>('/api/ecareer/account/send-otp', {
    Email: email,
    ExpiredDate: otpExpiredDate(),
    Purpose: purpose,
  })
}

/**
 * What the verification page is allowed to know: whose address is being
 * verified, and when the code expires.
 *
 * There is deliberately no `otpId` here. In the original the code itself was
 * rendered into a hidden `#OtpId` field and compared in the browser, so anyone
 * could read it with Inspect Element and reset another person's password. The
 * typed code now goes to the server, which holds the real one in the session
 * and compares it there — GET otp-context does not return it, and this type
 * must not invite anyone to expect it back.
 */
export interface OtpContext {
  success: boolean
  message?: string
  email: string
  exp: string
}

export function fetchOtpContext(hash: string): Promise<OtpContext> {
  return api.get<OtpContext>(`/api/ecareer/account/otp-context?hash=${encodeURIComponent(hash)}`)
}
